import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, MetadataForm, Participant, SourceData, SourceDataEventType, SourceDataFieldType, SourceDataItem, SourceDataItemGroupType, SourceDataReviewAndVerificationUpdate, Trial, UpdateCaseReportFormState } from "@/api/dtos";
import ReviewAndVerificationForm, { IReviewAndVerificationItemData } from "@/components/forms/ReviewAndVerificationForm";
import { SourceDataItemMapping } from "@/components/forms/forms/source-data/values/Index";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import { createItemConfiguration } from "@/hooks/form/configuration/useItemConfiguration";
import { ItemType } from "@/hooks/form/configuration/useItemConfigurations";
import { createItemGroupConfiguration } from "@/hooks/form/configuration/useItemGroupConfiguration";
import { createEvent } from "@/hooks/form/source-data/useEvent";
import { createForm } from "@/hooks/form/source-data/useForm";
import useItem from "@/hooks/form/source-data/useItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingButton } from "@mui/lab";
import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, TooltipProps, darken, lighten, styled, useTheme } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginBottom: theme.spacing(2), 
    fontSize: '1.5rem',
    gap: theme.spacing(2),
    //justifyContent: 'space-between',
    alignItems: 'center'
}))

const ChipBox = styled(Box)(({theme}) => ({
    display: 'flex',
    marginLeft: 'auto',
    gap: theme.spacing(2),

    '& > div': {
        
        paddingLeft: theme.spacing(0.5),
    },
    '& svg': {
        fontSize: '1.5rem'
    }
}))

const StyledTable = styled(Table)(({ theme }) => ({
    tableLayout: 'fixed',
    borderCollapse: 'separate',

    '& thead a': {
        top: 0,
        padding: theme.spacing(1,2),
        width: theme.spacing(30),
        boxShadow: `12px 0px 17px 0px rgba(0,0,0,0.14)`,
        background: theme.palette.background.paper,
        backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))' : 'linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 1))',
        borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(81, 81, 81, 1)' : '1px solid rgba(224, 224, 224, 1)',
        zIndex: 4,
    },
    '& th:nth-of-type(odd), & thead a:nth-of-type(even)': {
        background: theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.90) : lighten(theme.palette.common.black, 0.95)
    },
    '& tr:first-of-type th:first-of-type': {
        borderBottom: 'none'
    },
    '& tr:nth-of-type(even) th:first-of-type': {
        position: 'sticky',
        left: 0,
        background: theme.palette.background.paper,
        backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))' : undefined,
        borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(81, 81, 81, 1)' : '1px solid rgba(224, 224, 224, 1)',
        boxShadow: `0px 12px 17px 2px rgba(0,0,0,0.14)`,
        zIndex: 5
    },
    '& thead a:first-of-type': {
        width: theme.spacing(50),
        position: 'sticky',
        left: 0,
        background: theme.palette.background.paper,
        backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))' : undefined,
        borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(81, 81, 81, 1)' : '1px solid rgba(224, 224, 224, 1)',
        boxShadow: `none`,
        zIndex: 7
    },

    '& td': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: theme.spacing(2)
    },
    '& td:nth-of-type(odd)': {
        background: theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.90) : lighten(theme.palette.common.black, 0.95)
    },
    '& td:first-of-type': {
        position: 'sticky',
        left: 0,
        background: theme.palette.background.paper,
        backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))' : undefined,
        borderRight: theme.palette.mode === 'dark' ? '1px solid rgba(81, 81, 81, 1)' : '1px solid rgba(224, 224, 224, 1)',
        boxShadow: `0px 12px 17px 2px rgba(0,0,0,0.14)`,
        zIndex: 5
    }
}))

const DividerTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(81, 81, 81, 1)' : '1px solid rgba(224, 224, 224, 1)',
    height: 0, 
    padding: `0 !important`
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    overflow: 'auto',
    top: 0,
    left: 0,
    right: 0

    // '&::-webkit-scrollbar': {
    //     width: 8,
    //     height: 8
    // },

    // '&::-webkit-scrollbar-track': {
    //     '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.1)'
    // },

    // '&::-webkit-scrollbar-thumb': {
    //     backgroundColor: theme.palette.mode === 'dark' ? 'rgba(81, 81, 81, 1)' : 'rgba(224, 224, 224, 1)'
    // }
}))

const FloatingBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingBottom: theme.spacing(3),
}))

const StyledBox = styled(Box)(({ theme }) => ({
    //height: 'calc(100vh - 68px - 195px)',
    position: 'relative',
    //overflow: 'scroll'
}))

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    marginTop: '12px !important'
}));

const NotApplicableTableCell = styled(TableCell)(({ theme }) => {
    const colorOne = 'transparent';
    const colorTwo = theme.palette.mode === 'dark' ? 'rgb(25, 25, 25)' : 'rgb(242, 242, 242)';

    const colorOneAlt = theme.palette.mode === 'dark' ? lighten('rgb(25, 25, 25)', 0.05) : darken('rgb(242, 242, 242)', 0.05);
    const colorTwoAlt = theme.palette.mode === 'dark' ? 'rgb(25, 25, 25)' : 'rgb(242, 242, 242)';

    return {
        background: `repeating-linear-gradient(
            -45deg,
            ${colorOne},
            ${colorOne} 10px,
            ${colorTwo} 10px,
            ${colorTwo} 20px
        );`,
        
        '&:nth-of-type(odd)': {
            background: `repeating-linear-gradient(
                -45deg,
                ${colorOneAlt},
                ${colorOneAlt} 10px,
                ${colorTwoAlt} 10px,
                ${colorTwoAlt} 20px
            ) !important`
        },
    };
})

interface IFormData {
    studyEvent: ReturnType<typeof createEvent>;
    studyEventOid: string; 
    studyEventRepeat: number;
    caseReportForm: ReturnType<typeof createForm>;
    items?: Record<string, Record<number, Record<string, SourceDataItem>>>;
};

export interface Item {
    type: ItemType.Item;
    itemConfiguration: NonNullable<ReturnType<typeof createItemConfiguration>>;
    itemGroupName?: string;
    itemGroupRepeat: number;
    itemName: string;
    order: number;
}

export interface ItemGroup {
    type: ItemType.ItemGroup;
    itemGroupConfiguration: NonNullable<ReturnType<typeof createItemGroupConfiguration>>;
    items: Array<Item>;
    itemGroupName: string;
    itemGroupRepeat: number;
    order: number;
}

const createItemMetadata = (
    metadata: Metadata, 
    trialConfiguration: ConfigurationSourceDataMap, 
    configuration: ConfigurationSourceDataMap,
    trial: Trial,
    masterGroup: MasterGroup | undefined,
    coordinatingGroup: CoordinatingGroup | undefined,
    institution: Institution | undefined,
    participant: Participant,
    data: IFormData[],
    caseReportFormOids: string[],
    preview: boolean
) => {


    const caseReportFormVersionOids = caseReportFormOids.map(y => {

        return {
            caseReportFormOid: y,
            caseReportFormVersionOids: Array.from(
                new Set<string>(
                    data
                        .filter(x => x.caseReportForm?.caseReportFormOid === y)
                        .map(x => x.caseReportForm?.formVersion)
                        .sort((a, b) => (a?.formVersionMetadata.order ?? 0) > (b?.formVersionMetadata.order ?? 0) ? -1 : 1)
                        .map(x => x?.caseReportFormVersionOid)
                        .filter(x => !!x) as string[]
                )
            )
        };
    });

    const itemGroupNames: Set<string> = new Set<string>();
    const itemNames: Record<string, Set<string>> = {};

    for (let caseReportForm of caseReportFormVersionOids) {
        for (let caseReportFormVersionOid of caseReportForm.caseReportFormVersionOids) {
            const itemGroups = metadata.items?.[caseReportForm.caseReportFormOid]?.[caseReportFormVersionOid] ?? {};
    
            for (let itemGroupName in itemGroups) {
                itemGroupNames.add(itemGroupName);
    
                // Get Item Names for each group -----------------------------------------------
                itemNames[itemGroupName] = itemNames[itemGroupName] ?? new Set<string>();
    
                const items = metadata.items?.[caseReportForm.caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName] ?? {};
    
                for (let itemName in items) {
                    itemNames[itemGroupName].add(itemName);
                }
            }
        }
    }

    const itemGroupCounts: Record<string, number> = {};

    for (let itemGroupName of itemGroupNames) {
        itemGroupCounts[itemGroupName] = 1;

        for (let d of data) {
            const itemGroupRepeats = d.items?.[itemGroupName] ?? {};

            for (let igRepeat in itemGroupRepeats) {
                const itemGroupRepeat = +igRepeat;

                if (itemGroupRepeat > itemGroupCounts[itemGroupName]) {
                    itemGroupCounts[itemGroupName] = itemGroupRepeat;
                }
            }
        }
    }

    
    const list: Array<Item | ItemGroup> = [];

    for (let itemGroupName of itemGroupNames) {
        // ungrouped set of items;
        if (itemGroupName === AppSettings.metadata.itemGroupUngroupedKey) {
            for (let itemName of itemNames[itemGroupName]) {
                const itemConfigurations = caseReportFormVersionOids.reduce<ReturnType<typeof createItemConfiguration>[]>(
                    (a, b) => {
                        const formItems = b.caseReportFormVersionOids.map(x => createItemConfiguration(
                            metadata,
                            trialConfiguration.items,
                            configuration.items,
                            trial,
                            configuration?.configuration?.isGlobal,
                            configuration?.configuration?.id,
                            undefined,
                            b.caseReportFormOid,
                            x,
                            undefined,
                            itemName
                        ))
                        .filter(x => !!x) as Array<ReturnType<typeof createItemConfiguration>>

                        return [...a, ...formItems]
                    },
                    []
                );
                    

                const itemConfiguration = itemConfigurations.find(x => true);

                if (!itemConfiguration) {
                    continue;
                }

                list.push({
                    type: ItemType.Item,
                    itemGroupName: undefined,
                    itemGroupRepeat: 1,
                    itemName: itemName,
                    itemConfiguration: itemConfiguration,
                    order: itemConfiguration?.itemMetadata.order ?? 0
                })
            }
        }
        // Group items
        else {                
            const itemGroupConfigurations = caseReportFormVersionOids.reduce<ReturnType<typeof createItemGroupConfiguration>[]>(
                (a, b) => {
                    const formItems = b.caseReportFormVersionOids
                    .map(x => createItemGroupConfiguration(
                        metadata, 
                        trialConfiguration?.itemGroups, 
                        configuration?.itemGroups, 
                        trial, 
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id,
                        undefined,
                        b.caseReportFormOid,
                        x,
                        itemGroupName
                    ))
                    .filter(x => !!x) as Array<ReturnType<typeof createItemGroupConfiguration>>

                    return [...a, ...formItems]
                },
                []
            );

            const itemGroupConfiguration = itemGroupConfigurations.find(x => true);

            if (!itemGroupConfiguration) {
                continue;
            }

            const itemList: Item[] = [];

            for (let itemName of itemNames[itemGroupName]) {
                const itemConfigurations = caseReportFormVersionOids.reduce<ReturnType<typeof createItemConfiguration>[]>(
                    (a, b) => {
                        const formItems = b.caseReportFormVersionOids.map(x => createItemConfiguration(
                            metadata,
                            trialConfiguration.items,
                            configuration.items,
                            trial,
                            configuration?.configuration?.isGlobal,
                            configuration?.configuration?.id,
                            undefined,
                            b.caseReportFormOid,
                            x,
                            itemGroupName,
                            itemName
                        ))
                        .filter(x => !!x) as Array<ReturnType<typeof createItemConfiguration>>

                        return [...a, ...formItems]
                    },
                    []
                );

                const itemConfiguration = itemConfigurations.find(x => true);

                if (!itemConfiguration) {
                    continue;
                }

                itemList.push({
                    type: ItemType.Item,
                    itemGroupName: itemGroupName,
                    itemGroupRepeat: 1,
                    itemName: itemName,
                    itemConfiguration: itemConfiguration,
                    order: itemConfiguration?.itemMetadata.order ?? 0
                })
            }

            itemList
                .sort((a, b) => a.order < b.order ? -1 : 1);

            const itemGroupOrder = itemList.reduce<number | undefined>((min, current) => {
                if (!min || min > current.order) {
                    return current.order;
                }

                return min;
            }, undefined) ?? 0;

            for (let itemGroupRepeat = 1; itemGroupRepeat <= itemGroupCounts[itemGroupName]; itemGroupRepeat++) {
                list.push({
                    type: ItemType.ItemGroup,
                    itemGroupName: itemGroupName,
                    itemGroupRepeat: itemGroupRepeat,
                    itemGroupConfiguration: itemGroupConfiguration,
                    items: itemList.map(x => ({
                        ...x,
                        itemGroupRepeat: itemGroupRepeat
                    })),
                    order: itemGroupOrder
                })
            }
        }
    }

    
    list
        .sort((a, b) => {
            if (a.order === b.order) {
                if (a.itemGroupName === b.itemGroupName) {
                    return a.itemGroupRepeat < b.itemGroupRepeat ? -1 : 1 
                }

                if (a.itemGroupName === undefined) {
                    return -1;
                }

                return 1;
            }
            else {
                return a.order < b.order ? -1 : 1 
            }
        });

    return list;
}

const getReviewAndVerificationState = (
    form: IFormData, 
    items: IItems | undefined, 
    data: Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>> | null | undefined,
    allowReview?: boolean,
    allowVerification?: boolean
): {
    hasReview: boolean;
    hasVerification: boolean;
    allReviewed: boolean;
    allVerified: boolean;
    reviewLoading: boolean;
    verificationLoading: boolean;
    reviewTotal: number;
    reviewComplete: number;
    verificationTotal: number;
    verificationComplete: number;
} => {

    let hasReview = false;
    let hasVerification = false;
    let allReviewed = true;
    let allVerified = true;
    let reviewLoading = false;
    let verificationLoading = false;
    let reviewTotal = 0;
    let reviewComplete = 0;
    let verificationTotal = 0;
    let verificationComplete = 0;

    if (!allowVerification && !allowReview) {
        return {
            hasReview,
            hasVerification,
            allReviewed,
            allVerified,
            reviewLoading,
            verificationLoading,
            reviewComplete,
            reviewTotal,
            verificationComplete,
            verificationTotal
        }
    }

    for (let item of (items ?? [])) {
        if (item.type == ItemType.ItemGroup) {
            const groupState = getReviewAndVerificationState(
                form, 
                item.items, 
                data, 
                allowReview && item.itemGroupConfiguration.configuration?.allowReview,
                allowVerification && item.itemGroupConfiguration.configuration?.allowVerification
            );

            hasReview = hasReview || groupState.hasReview;
            hasVerification = hasVerification || groupState.hasVerification;

            if (!groupState.allReviewed) {
                allReviewed = false;
            }

            if (!groupState.allVerified) {
                allVerified = false;
            }
            
            reviewLoading = reviewLoading || groupState.reviewLoading;
            verificationLoading = verificationLoading || groupState.verificationLoading;

            reviewTotal = reviewTotal + groupState.reviewTotal;
            reviewComplete = reviewComplete + groupState.reviewComplete;
            verificationTotal = verificationTotal + groupState.verificationTotal;
            verificationComplete = verificationComplete + groupState.verificationComplete;

            continue;
        }

        const state = data?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!];

        const sourceDataItem = form.items?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!]
        const isDisabled = !sourceDataItem;

        const allowItemReview = !isDisabled && 
            allowReview &&
            item.itemConfiguration.configuration?.allowReview;

        if (allowItemReview) {
            hasReview = true;

            reviewTotal++;

            if (!state?.review?.reviewed) {
                allReviewed = false;
            }
            else {
                reviewComplete++;
            }

            if (state?.reviewLoading) {
                reviewLoading = true;
            }
        }

        const allowItemVerification = !isDisabled && 
            allowVerification &&
            item.itemConfiguration.configuration?.allowVerification;

        if (allowItemVerification) {
            hasVerification = true;

            verificationTotal++;

            if (!state?.verification?.verified) {
                allVerified = false;
            }
            else {
                verificationComplete++;
            }

            if (state?.verificationLoading) {
                verificationLoading = true;
            }
        }
    }

    return {
        hasReview,
        hasVerification,
        allReviewed,
        allVerified,
        reviewLoading,
        verificationLoading,
        reviewComplete,
        reviewTotal,
        verificationComplete,
        verificationTotal
    }
}

const getReviewUpdates = (
    form: IFormData, 
    items: IItems | undefined, 
    data: Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>> | null | undefined,
    allReviewed: boolean,
    allowReview?: boolean,
    allowVerification?: boolean
): SourceDataReviewAndVerificationUpdate[] => {

    const updates: SourceDataReviewAndVerificationUpdate[] = [];

    for (let item of items ?? []) {
        if (item.type == ItemType.ItemGroup) {
            const groupUpdates = getReviewUpdates(
                form, 
                item.items, 
                data, 
                allReviewed,
                allowReview && item.itemGroupConfiguration.configuration?.allowReview,
                allowVerification && item.itemGroupConfiguration.configuration?.allowVerification
            );

            for (let groupUpdate of groupUpdates) {
                updates.push(groupUpdate);
            }

            continue;
        }

        let hasReview = false;
        let hasVerification = false;

        let reviewed: boolean | undefined = undefined;
        let verified: boolean | undefined = undefined;

        const state = data?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!];

        const sourceDataItem = form.items?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!]
        const isDisabled = !sourceDataItem;

        const allowItemReview = !isDisabled && 
            allowReview &&
            item.itemConfiguration.configuration?.allowReview;

        if (allowItemReview) {
            hasReview = true;

            if (!state?.review?.reviewed) {
                reviewed = false;
            }
            else {
                reviewed = true;
            }
        }

        const allowItemVerification = !isDisabled && 
            allowVerification &&
            item.itemConfiguration.configuration?.allowVerification;

        if (allowItemVerification) {
            hasVerification = true;

            if (!state?.verification?.verified) {
                verified = false;
            }
            else {
                verified = true;
            }
        }

        if (hasVerification || hasReview) {
            const newValue = !allReviewed;

            updates.push(new SourceDataReviewAndVerificationUpdate({
                itemGroupName: item?.itemGroupName,
                itemGroupRepeat: item?.itemGroupRepeat,
                itemName: item.itemName,
                priority: item.itemConfiguration.priority,
                value: sourceDataItem?.value,
                reviewed: reviewed === undefined ? undefined : newValue,
                verified: !newValue && verified ? false : verified
            }));
        }
    }

    return updates;
}

const getVerificationUpdates = (
    form: IFormData, 
    items: IItems | undefined, 
    data: Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>> | null | undefined,
    allVerified: boolean,
    allowReview?: boolean,
    allowVerification?: boolean
): SourceDataReviewAndVerificationUpdate[] => {


    const updates: SourceDataReviewAndVerificationUpdate[] = [];

    for (let item of items ?? []) {
        if (item.type == ItemType.ItemGroup) {
            const groupUpdates = getVerificationUpdates(
                form, 
                item.items, 
                data, 
                allVerified,
                allowReview && item.itemGroupConfiguration.configuration?.allowReview,
                allowVerification && item.itemGroupConfiguration.configuration?.allowVerification
            );

            for (let groupUpdate of groupUpdates) {
                updates.push(groupUpdate);
            }

            continue;
        }

        let hasReview = false;
        let hasVerification = false;

        let reviewed: boolean | undefined = undefined;
        let verified: boolean | undefined = undefined;

        const state = data?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!];

        const sourceDataItem = form.items?.[item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.itemGroupRepeat]?.[item.itemName!]
        const isDisabled = !sourceDataItem;

        const allowItemReview = !isDisabled && 
            allowReview &&
            item.itemConfiguration.configuration?.allowReview;

        if (allowItemReview) {
            hasReview = true;

            if (!state?.review?.reviewed) {
                reviewed = false;
            }
            else {
                reviewed = true;
            }
        }


        const allowItemVerification = !isDisabled && 
            allowVerification &&
            item.itemConfiguration.configuration?.allowVerification;

        if (allowItemVerification) {
            hasVerification = true;

            if (!state?.verification?.verified) {
                verified = false;
            }
            else {
                verified = true;
            }
        }

        if (hasVerification || hasReview) {
            const newValue = !allVerified;

            updates.push(new SourceDataReviewAndVerificationUpdate({
                itemGroupName: item?.itemGroupName,
                itemGroupRepeat: item?.itemGroupRepeat,
                itemName: item.itemName,
                priority: item.itemConfiguration.priority,
                value: sourceDataItem?.value,
                reviewed: newValue && reviewed === false ? true : reviewed,
                verified: verified === undefined ? undefined : newValue
            }));
        }
    }


    return updates;
}


const ParticipantSourceDataFormTableVerification: FC<{ 
    form: IFormData;  
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
    items: IItems;
}>= ({ 
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    form,
    items
}) => {


    const client = useContext(ServiceStackClientContext);

    const { 
        settings: { 
            hideReviews,
            hideVerifications
        } 
    } = useContext(SourceDataFormSettingsContext);

    const propertyPath = useMemo(() => {
        if (!form.studyEventOid ||
            !form.studyEventRepeat ||
            !form.caseReportForm?.caseReportFormOid
        ) {
            return 'data.unknown'
        }

        return `data.${form.studyEventOid}.${form.studyEventRepeat}.${form.caseReportForm?.caseReportFormOid}`;
    }, [
        form.studyEventOid,
        form.studyEventRepeat,
        form.caseReportForm?.caseReportFormOid
    ])

    const { 
        state: {
            value: data 
        },
        context: {
            setValue: setData,
            getValue: getData
        }
    } = useScopedField<Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>>>(propertyPath, { value: true });

    const { 
        state: {
            value: loading 
        },
        context: {
            setValue: setLoading
        }
    } = useScopedField<boolean>('loading', { value: true });
    
    
    const {
        hasReview,
        hasVerification,
        allReviewed,
        allVerified,
        reviewLoading,
        verificationLoading
    } = useMemo(() => {
        const allowReview = form.studyEvent?.configuration?.allowReview === true &&
            form.caseReportForm?.configuration?.allowReview === true &&
            form.caseReportForm?.formVersion?.configuration?.allowReview === true;

        const allowVerification = form.studyEvent?.configuration?.allowVerification === true &&
            form.caseReportForm?.configuration?.allowVerification === true &&
            form.caseReportForm?.formVersion?.configuration?.allowVerification === true;
        
        return getReviewAndVerificationState(form, items, data, allowReview, allowVerification);
    }, [data, items, form]);

    const onReviewClick = async () => {
        
        setLoading(true);

        const allowReview = form.studyEvent?.configuration?.allowReview === true &&
            form.caseReportForm?.configuration?.allowReview === true &&
            form.caseReportForm?.formVersion?.configuration?.allowReview === true;

        const allowVerification = form.studyEvent?.configuration?.allowVerification === true &&
            form.caseReportForm?.configuration?.allowVerification === true &&
            form.caseReportForm?.formVersion?.configuration?.allowVerification === true;

        const updates: SourceDataReviewAndVerificationUpdate[] = getReviewUpdates(form, items, data, allReviewed, allowReview, allowVerification);

        if (updates.length > 0) {
            
            try {
                const response = await client.post(new UpdateCaseReportFormState({
                    trialId: trial?.id,
                    masterGroupId: masterGroup?.id,
                    coordinatingGroupId: coordinatingGroup?.id,
                    institutionId: institution?.id,
                    participantId: participant?.id,
                    studyEventOid: form.studyEventOid,
                    studyEventRepeat: form.studyEventRepeat,
                    caseReportFormOid: form.caseReportForm?.caseReportFormOid,
                    caseReportFormVersionOid: form.caseReportForm?.formVersion.caseReportFormVersionOid,
                    updates: updates
                }));
            }
            catch {

            }
        }

        setLoading(false);
    };

    const onVerificationClick = async () => {
        setLoading(true);

        const allowReview = form.studyEvent?.configuration?.allowReview === true &&
            form.caseReportForm?.configuration?.allowReview === true &&
            form.caseReportForm?.formVersion?.configuration?.allowReview === true;

        const allowVerification = form.studyEvent?.configuration?.allowVerification === true &&
            form.caseReportForm?.configuration?.allowVerification === true &&
            form.caseReportForm?.formVersion?.configuration?.allowVerification === true;

        const updates: SourceDataReviewAndVerificationUpdate[] = getVerificationUpdates(form, items, data, allVerified, allowReview, allowVerification)

        if (updates.length > 0) {
            
            try {
                const response = await client.post(new UpdateCaseReportFormState({
                    trialId: trial?.id,
                    masterGroupId: masterGroup?.id,
                    coordinatingGroupId: coordinatingGroup?.id,
                    institutionId: institution?.id,
                    participantId: participant?.id,
                    studyEventOid: form.studyEventOid,
                    studyEventRepeat: form.studyEventRepeat,
                    caseReportFormOid: form.caseReportForm?.caseReportFormOid,
                    caseReportFormVersionOid: form.caseReportForm?.formVersion.caseReportFormVersionOid,
                    updates: updates
                }));
            }
            catch {

            }
        }

        setLoading(false);
    };


    const disabled = false;
    
    return (
        <TableCell
            sx={{
                px: 0,
                py: 0
            }}
        >
            {
                hasReview && ( 
                    <StyledTooltip
                        title={
                            disabled ? 
                                'Entry missing from OpenClinica' :
                                allReviewed ? 
                                    'Reviewed' : 
                                    'Not Reviewed'
                        }
                        followCursor
                    >
                        <LoadingButton
                            component="div"
                            sx={{
                                borderRadius: 0,
                                width: '50%'
                            }}
                            disableElevation
                            variant={
                                allReviewed ?
                                    'contained' :
                                    'text'
                            }
                            color={
                                allReviewed ?
                                    'success' :
                                    'primary'
                            }
                            loading={!!reviewLoading || !!loading}
                            onClick={onReviewClick}
                        >
                            <FontAwesomeIcon icon={AppSettings.icons.review} size="2x" />
                        </LoadingButton>
                    </StyledTooltip>
                )
            }
            {
                hasVerification && (
                    <StyledTooltip
                        title={
                            disabled ? 
                                'Entry missing from OpenClinica' :
                                allVerified ? 
                                    'Verified' : 
                                    'Not Verified'
                        }
                        followCursor
                    >
                        <LoadingButton
                            component="div"
                            sx={{
                                borderRadius: 0,
                                width: '50%'
                            }}
                            disableElevation
                            variant={
                                allVerified ?
                                    'contained' :
                                    'text'
                            }
                            color={
                                allVerified ?
                                    'success' :
                                    'primary'
                            }
                            loading={!!verificationLoading || !!loading}
                            onClick={onVerificationClick}
                        >
                            <FontAwesomeIcon icon={AppSettings.icons.verification} size="2x" />
                        </LoadingButton>
                    </StyledTooltip>
                )
            }
        </TableCell>
    );
}

const ParticipantSourceDataFormTableHeader: FC<{ 
    forms: IFormData[];  
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
    items: IItems;
}> = ({ 
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    forms,
    items
}) => {
    const navigate = useNavigate();
    const link = `/${trial?.oid}${institution ? `/institutions/${institution.code}` : ''}/participants/${participant.studyNumber}/events`;
    

    const { 
        settings: { 
            hideReviews,
            hideVerifications
        } 
    } = useContext(SourceDataFormSettingsContext);

    return (
        <TableHead>
            <TableRow>
                <a style={{ display: 'table-cell'}}>
                    
                </a>
                {
                    forms?.map((x, i) => {
                        const formLink = `${link}/${x.studyEventOid}/${x.studyEventRepeat}/forms/${x.caseReportForm?.caseReportFormOid}`;
                        return (
                                <Button
                                    key={`${x.caseReportForm?.caseReportFormOid}-${x.studyEventOid}-${x.studyEventRepeat}`}
                                    component={Link}
                                    sx={{ display: 'table-cell', borderRadius: 0}}
                                    disableElevation
                                    to={formLink}
                                >
                                    <div>
                                        {x.caseReportForm?.displayName}
                                    </div>
                                    <div>
                                        {x.studyEvent?.displayName}{x.studyEvent?.eventMetadata?.type === SourceDataEventType.Repeating && <> &mdash; {x.studyEventRepeat}</>} ({x.caseReportForm?.formVersion?.displayName})
                                    </div>
                                </Button>
                        )
                    })
                }
            </TableRow>
            {
                (!hideReviews || !hideVerifications) && (
                    <TableRow>
                        <TableCell>
                            
                        </TableCell>
                        {
                            forms?.map((x, i) => {
                                return (
                                    <ParticipantSourceDataFormTableVerification
                                        key={`${x.caseReportForm?.caseReportFormOid}-${x.studyEventOid}-${x.studyEventRepeat}-verify`}
                                        trial={trial}
                                        masterGroup={masterGroup}
                                        coordinatingGroup={coordinatingGroup}
                                        institution={institution}
                                        participant={participant}
                                        form={x}
                                        items={items}
                                    />
                                )
                            })
                        }
                    </TableRow>
                )
            }
        </TableHead>
    );
}


export const ParticipantSourceDataFormTableItemGroup: FC<{ 
    itemGroupData?: ItemGroup; 
    forms: IFormData[]; 
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
}> = ({
    metadata,
    trialConfiguration,
    configuration,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    itemGroupData,
    forms
}) => {
    if (!itemGroupData) {
        return null;
    }

    return (
        <>
            {/* <TableRow>
                <DividerTableCell />
                {
                    forms?.map((x, i) => {
                        return (
                            <DividerTableCell 
                                key={`${x.studyEventOid}-${x.studyEventRepeat}`}
                            />
                        )
                    })
                }
            </TableRow> */}
            <TableRow>
                <TableCell>
                    {itemGroupData.itemGroupConfiguration.displayName}{itemGroupData.itemGroupConfiguration.itemGroupMetadata.type === SourceDataItemGroupType.Grid && <> &mdash; {itemGroupData.itemGroupRepeat}</>}
                </TableCell>
                {
                    forms?.map((x, i) => {
                        return (
                            <TableCell
                                key={`${x.caseReportForm?.caseReportFormOid}-${x.studyEventOid}-${x.studyEventRepeat}`}
                            >
                                
                            </TableCell>
                        )
                    })
                }
            </TableRow>
            <ParticipantSourceDataFormTableBody 
                metadata={metadata}
                trialConfiguration={trialConfiguration}
                configuration={configuration}
                trial={trial}
                masterGroup={masterGroup}
                coordinatingGroup={coordinatingGroup}
                institution={institution}
                participant={participant}
                items={itemGroupData.items} 
                forms={forms} 
                grouped={true} 
            />
            {/* <TableRow>
                <DividerTableCell />
                {
                    forms?.map((x, i) => {
                        return (
                            <DividerTableCell 
                                key={`${x.studyEventOid}-${x.studyEventRepeat}`}
                            />
                        )
                    })
                }
            </TableRow> */}
        </>
    );
};

export const ParticipantSourceDataFormTableItem: FC<{ 
    itemData?: Item; 
    forms: IFormData[]; 
    grouped?: boolean; 
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
}> = ({
    metadata,
    trialConfiguration,
    configuration,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    itemData,
    forms,
    grouped
}) => {
    if (!itemData || !itemData.itemConfiguration || !itemData.itemConfiguration.itemMetadata || itemData.itemConfiguration.itemMetadata.fieldType === SourceDataFieldType.Hidden) {
        return null;
    }

    const theme = useTheme();

    return (
        <TableRow>
            <TableCell
                sx={{
                    paddingLeft: grouped ? `${theme.spacing(8)} !important` : undefined
                }}
            >
                {itemData.itemConfiguration.displayName}
            </TableCell>
            {
                forms?.map((x, i) => {
                    return (
                        <ParticipantSourceDataFormTableCell
                            key={`${x.caseReportForm?.caseReportFormOid}-${x.studyEventOid}-${x.studyEventRepeat}`}
                            metadata={metadata}
                            trialConfiguration={trialConfiguration}
                            configuration={configuration}
                            trial={trial}
                            masterGroup={masterGroup}
                            coordinatingGroup={coordinatingGroup}
                            institution={institution}
                            participant={participant}
                            itemData={itemData}
                            form={x}
                        />
                    )
                })
            }
        </TableRow>
    );
};

const ParticipantSourceDataFormTableCell: FC<{ 
    itemData?: Item; 
    form: IFormData;
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
}> = ({
    metadata,
    trialConfiguration,
    configuration,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    itemData,
    form
}) => {
    const item = useItem(
        metadata,
        trialConfiguration,
        configuration,
        new SourceData({
            items: {
                [form.studyEventOid]: {
                    [form.studyEventRepeat]: {
                        [form.caseReportForm!.caseReportFormOid!]: form.items ?? {}
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        form.studyEventOid,
        form.studyEventRepeat,
        form.caseReportForm?.caseReportFormOid,
        form.caseReportForm?.formVersion.caseReportFormVersionOid,
        itemData?.itemGroupName,
        itemData?.itemGroupRepeat,
        itemData?.itemName
    );

    if (!item) {
        return (
            <NotApplicableTableCell>
            </NotApplicableTableCell>
        )
    }

    const ValueComponent = SourceDataItemMapping[itemData!.itemConfiguration.itemMetadata.fieldType ?? SourceDataFieldType.Text];

    return (
        <TableCell
        >
            <ValueComponent itemData={item} hideBorderStyles={true} hideStyles={false} />
        </TableCell>
    );
}

type IItems = ReturnType<typeof createItemMetadata>

interface IParticipantSourceDataFormTableBodyProps {
    forms: IFormData[];
    items: IItems;
    grouped?: boolean;
    metadata: Metadata;
    trialConfiguration: ConfigurationSourceDataMap;
    configuration: ConfigurationSourceDataMap;
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
}


const ParticipantSourceDataFormTableBody: FC<IParticipantSourceDataFormTableBodyProps> = ({
    metadata,
    trialConfiguration,
    configuration,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    forms,
    items,
    grouped
}) => {

    const list = useMemo(() => {
        if (!items) {
            return [];
        }

        return (
            items.map((x, i, a) => {
                if (x.type === ItemType.ItemGroup) {
                    return (
                        <ParticipantSourceDataFormTableItemGroup
                            key={`${x.itemGroupConfiguration?.itemGroupName}-${x.itemGroupRepeat}`}
                            metadata={metadata}
                            trialConfiguration={trialConfiguration}
                            configuration={configuration}
                            trial={trial}
                            masterGroup={masterGroup}
                            coordinatingGroup={coordinatingGroup}
                            institution={institution}
                            participant={participant}
                            itemGroupData={x}
                            forms={forms}
                        />
                    );
                }

                return (
                    <ParticipantSourceDataFormTableItem
                        key={x.itemConfiguration.itemName}
                        metadata={metadata}
                        trialConfiguration={trialConfiguration}
                        configuration={configuration}
                        trial={trial}
                        masterGroup={masterGroup}
                        coordinatingGroup={coordinatingGroup}
                        institution={institution}
                        participant={participant}
                        itemData={x}
                        forms={forms}
                        grouped={grouped}
                    />
                )
            })
        )
    }, [items]);

    return (
        <>
            {
                list
            }
        </>
    )
}

const TableHeaderSticky: FC<PropsWithChildren> = ({children}) => {

    const elRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>();
    const headerRef = useRef<HTMLElement>();

    useEffect(() => {
        const onAnimate = () => {
            if (!elRef.current) {
                return;
            }

            if (!tableRef.current) {
                tableRef.current = elRef.current.querySelector('table') as HTMLTableElement | undefined;
            }

            if (!headerRef.current) {
                headerRef.current = document.querySelector('header') as HTMLElement | undefined;
            }

            const tableRect = tableRef.current?.getBoundingClientRect() ?? { top: 0, left: 0, width: 0, height: 0 };
            const headerRect = headerRef.current?.getBoundingClientRect() ?? { top: 0, left: 0, width: 0, height: 0 };

            tableRef.current?.querySelectorAll('thead a').forEach((ele: any) => { 
                ele.style.position = 'sticky'; 
                ele.style.top = ((-1 * tableRect.top) + headerRect.height) + "px"; 
            })
        }

        const onScroll = () => {
            requestAnimationFrame(onAnimate);
        };

        addEventListener("scroll", onScroll);

        return () => {
            removeEventListener('scroll', onScroll);
        }
    }, [elRef, tableRef, headerRef])

    return (
        <div ref={elRef}>
            {children}
        </div>
    )
}

const ParticipantSourceDataFormTable: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const [caseReportFormOids, setCaseReportFormOids] = useState<string[]>([])


        
    const formList = useMemo(() => {
        return metadata?.forms ? 
            Object.keys(metadata.forms!)
                .map(x => metadata.forms?.[x])
                .filter(x => !!x)
                .sort((a, b) => a!.name! < b!.name! ? -1 : 1) as MetadataForm[] : 
            [];
    }, []);

    const data = useMemo(() => {
        // No form data or form oid provided.
        if (!metadata  || 
            !trialConfiguration ||
            !configuration || 
            !trial ||
            !participant ||
            !sourceData || 
            !sourceData.forms || 
            !caseReportFormOids ||
            caseReportFormOids.length < 1
        ) 
        {
            return undefined
        }

        const studyEventOids = Object.keys(sourceData.forms);

        const forms: IFormData[] = []
        for (let caseReportFormOid of caseReportFormOids) {
            for (let studyEventOid of studyEventOids) {
                // study event has no repeats
                if (!sourceData.forms[studyEventOid]) {
                    continue;
                }

                const studyEventRepeats: string[] = Object.keys(sourceData.forms[studyEventOid]);

                for (let studyEventRepeatStr of studyEventRepeats) {
                    const studyEventRepeat = +studyEventRepeatStr;

                    //repeat does not have forms or does not have the requested form.
                    if (
                        !sourceData.forms[studyEventOid][studyEventRepeat] ||
                        !sourceData.forms[studyEventOid][studyEventRepeat][caseReportFormOid]
                    ) {
                        continue;
                    } 
                    
                    const studyEvent = createEvent(
                        metadata,
                        trialConfiguration.events,
                        configuration.events,
                        sourceData.events,
                        trial,
                        masterGroup,
                        coordinatingGroup,
                        institution,
                        participant,
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id,
                        studyEventOid,
                        studyEventRepeat,
                        false
                    );

                    if (!studyEvent) {
                        continue;
                    }

                    const caseReportForm = createForm(
                        metadata,
                        trialConfiguration.forms,
                        configuration.forms,
                        sourceData.forms,
                        trialConfiguration.formVersions,
                        configuration.formVersions,
                        trial,
                        masterGroup,
                        coordinatingGroup,
                        institution,
                        participant,
                        configuration?.configuration?.isGlobal,
                        configuration?.configuration?.id,
                        studyEventOid,
                        studyEventRepeat,
                        caseReportFormOid,
                        false
                    )

                    if (!caseReportForm) {
                        continue;
                    }

                    forms.push({
                        studyEvent: studyEvent,
                        studyEventOid: studyEventOid, 
                        studyEventRepeat: studyEventRepeat,
                        caseReportForm: caseReportForm,
                        items: sourceData.items?.[studyEventOid]?.[studyEventRepeat]?.[caseReportFormOid]
                    })
                }
            }
        }
        

        forms.sort((a, b) => {
            if (a.studyEvent?.eventMetadata.order === b.studyEvent?.eventMetadata.order) {
                if (a.studyEventRepeat === b.studyEventRepeat ) {
                    return a.caseReportForm?.eventFormMetadata?.order! > b.caseReportForm?.eventFormMetadata?.order! ? 1 : -1;
                }
                else {
                    return a.studyEventRepeat > b.studyEventRepeat ? 1 : -1;
                }
            }
            else {
                return (a.studyEvent?.eventMetadata.order ?? 0) > (b.studyEvent?.eventMetadata.order ?? 0) ? 1 : -1;
            }
        })

        const items = createItemMetadata(
            metadata,
            trialConfiguration,
            configuration,
            trial,
            masterGroup,
            coordinatingGroup,
            institution,
            participant,
            forms,
            caseReportFormOids,
            false
        )

        return {
            forms,
            items
        };
    }, [sourceData, caseReportFormOids, metadata, trialConfiguration, configuration, trial])

    if (!trial) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Trial</AlertTitle>
                No trial was found.
            </Alert>
        )
    }

    if (!participant) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>No Participant</AlertTitle>
                No participant was found.
            </Alert>
        )
    }

    if (!metadata) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Metadata Could Not Be Retrieved</AlertTitle>
                The metadata for the {trial.name} trial could not be retrieved from OpenClinica.
            </Alert>
        )
    }

    if (!trialConfiguration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Trial Configuration Could Not Be Retrieved</AlertTitle>
                The global configuration for the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!configuration) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Allocated Configuration Could Not Be Retrieved</AlertTitle>
                The allocated configuration for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    if (!sourceData) {
        return (
            <Alert
                severity="error"
            >
                <AlertTitle>Source Data Could Not Be Retrieved</AlertTitle>
                The source data for participant {participant.studyNumber} on the {trial.name} trial could not be retrieved.
            </Alert>
        )
    }

    
    return (
        <>
            <TitleBox>
                <FontAwesomeIcon icon={AppSettings.icons.form} /> Table Data
            </TitleBox>
                {
                    formList.map(x => (
                        <FormControlLabel
                            key={x.caseReportFormOid}
                            control={
                                <Checkbox 
                                    checked={caseReportFormOids.includes(x.caseReportFormOid!)} 
                                    onChange={(_, checked) => {
                                        if (checked) {
                                            setCaseReportFormOids(c => [...c, x.caseReportFormOid!])
                                        }
                                        else {
                                            setCaseReportFormOids(c => c.filter(y => y !== x.caseReportFormOid))
                                        }
                                    }}
                                />
                            }
                            label={x.name}
                        />
                    ))
                }
            <StyledBox>
                <FloatingBox>
                    <ReviewAndVerificationForm
                        trialId={trial?.id}
                        masterGroupId={masterGroup?.id}
                        coordinatingGroupId={coordinatingGroup?.id}
                        institutionId={institution?.id}
                        participantId={participant?.id}
                        studyEventOid={undefined}
                        studyEventRepeat={undefined}
                        caseReportFormOid={undefined}
                    >
                        {
                            data && (
                                <StyledPaper>
                                    <TableHeaderSticky>
                                        <StyledTable>
                                            <ParticipantSourceDataFormTableHeader 
                                                trial={trial}
                                                masterGroup={masterGroup}
                                                coordinatingGroup={coordinatingGroup}
                                                institution={institution}
                                                participant={participant}
                                                forms={data.forms} 
                                                items={data.items} 
                                            />
                                            <TableBody>
                                                <ParticipantSourceDataFormTableBody 
                                                    metadata={metadata}
                                                    trialConfiguration={trialConfiguration}
                                                    configuration={configuration}
                                                    trial={trial}
                                                    masterGroup={masterGroup}
                                                    coordinatingGroup={coordinatingGroup}
                                                    institution={institution}
                                                    participant={participant}
                                                    forms={data.forms} 
                                                    items={data.items} 
                                                />
                                            </TableBody>
                                        </StyledTable>
                                    </TableHeaderSticky>
                                </StyledPaper>
                            )
                        }
                    </ReviewAndVerificationForm>
                </FloatingBox>
            </StyledBox>
        </>
    )
};

export default ParticipantSourceDataFormTable;